import React                        from 'react';
import {
    Link }                          from "react-router-dom";
import DatePicker                   from "react-datepicker";

import * as AgencyType              from 'shared/types/Agency';
import * as LatLngType              from 'shared/types/LatLng';
import * as DistanceType            from 'shared/types/Distance';
import * as DurationType            from 'shared/types/Duration';
import * as ManifestType            from 'shared/types/Manifest';
import * as UserType                from 'shared/types/User';
import * as VehicleType             from 'shared/types/Vehicle';
import * as OrderType               from 'shared/types/Order';
import rehash                       from 'shared/utils/rehash';
import dayjs                        from 'shared/utils/day-timezone';

import Alert                        from 'utils/Alert';
import tectransit                   from 'utils/TecTransit';
import getApiPromise                from 'utils/getApiPromise';
import deletedOrderStyle            from 'utils/deletedOrderStyle';
import * as googleMapsLinks         from 'utils/googleMapsLinks';
import * as MenuItem                from 'components/MenuItem';
import * as VehicleManifestsType    from 'Dispatcher/components/VehicleManifests';

interface RouteStepProps {
    step            : ManifestType.Step.Hydrated<OrderType.Order<string>>;
    nextStep        : (ManifestType.Step.Hydrated<OrderType.Order<string>>|undefined);
    vehicleSchedule : Record<string,[number,number,number][]>;
    driversById     : Record<string,UserType.User>;
}
const RouteStep : React.FC<RouteStepProps> = props => {
    const {step,nextStep} = props;
    const backgroundsByName = {
        'depot'      : '#ffbf00',
        'standby'    : '#ffbf00',
        'break'      : '#ffbf00',
    };
    const tableStyle      = {
        // @ts-expect-error
        background              : (backgroundsByName[step.name]||'#dcdcdc'),
        borderRadius            : '0.5rem',
        boxShadow               : '0 4px 25px 0 rgb(0 0 0 / 10%)',
        padding                 : '0.5rem',
        marginBottom            : '15px',
        width                   : '100%'
    };
    const getOrderSpan = ( so:OrderType.Order<string>, ndx:number ) => {
        const contents = !so.user?._id ? <span style={deletedOrderStyle} title={so._id}>deleted order</span> : (<Link to={`/Dispatcher/StandingOrder/${so._id}`}>
            {so?.user?.name}
        </Link>);
        return (<span key={so._id}>
            {(ndx===0)?(' of '):(', ')}
            { contents }
        </span>);
    }
    const getDriverLink = ( driverId:(number|undefined) ) => {
        const driver = props.driversById[driverId!];
        return (
            <Link
                key     = {`driver_link_for_${driver?._id??Date.now()}`}
                title   = {driver?.name||`Driver #${driverId}`}
                to      = {`/Dispatcher/User/${driverId}`}
            >
                {driver?.name||'??'}
            </Link>
        );
    }
    const [
        stepName,
        stepLatLng,
        stepAddress
    ] = (step.name==='fixedroute') ? [
        step.frTrip!.name,
        step.frTrip!.stops[0],
        step.frTrip!.stops[0].name
    ] : [
        step.name,
        step.latlng,
        step.address
    ];
    const secondsStyle  = step.problem ? {color:'red',fontWeight:'bold'} : {};
    const stepMoment    = step.seconds ? dayjs(step.seconds*1000).tz(tectransit.agency.time_zone) : undefined;
    const stepRanges    = stepMoment ? props.vehicleSchedule[stepMoment.format('dddd').toLowerCase()] : undefined;
    const stepMinutes   = stepMoment ? stepMoment.diff(stepMoment.startOf('day'),'minutes') : undefined;
    const stepRange     = stepRanges?.find( sr => {
        return (sr[0]<=stepMinutes!) && (stepMinutes!<sr[1]);
    });
    // Compare this to OnDemandWhiteboardContext.getDraftVehicleManifestStepCards
    return (
        <table style={tableStyle}>
            <tbody>
                {step.latlng ? (
                    <tr>
                        <td style={{maxWidth:'30px',overflow:'hidden',whiteSpace:'nowrap',verticalAlign:'top'}}>{getDriverLink(stepRange?.[2])}</td>
                        <td colSpan={2} valign="top">{googleMapsLinks.getPlace(stepLatLng,stepAddress,googleMapsLinks.singleLineAddressFormatter)}</td>
                    </tr>
                ) : (
                    <tr>
                        <td colSpan={3}>{getDriverLink(stepRange?.[2])}</td>
                    </tr>
                )}
                <tr>
                    <td style={{width:'10%',verticalAlign:'top',...secondsStyle}} title={step.problem||stepMoment?.format(tectransit.timeFormat)||''}>
                        {stepMoment?.format('HH:mm')||'??'}
                    </td>
                    <td style={{width:'65%'}}>
                        <strong>{stepName}</strong>{step.etc?` (${step.etc})`:''}
                        {((step.pickups?.length||0)>0) && (<div>
                            <strong>pickup</strong>
                            {step.pickups?.map(getOrderSpan)}
                        </div>)}
                        {((step.dropoffs?.length||0)>0) && (<div>
                            <strong>dropoff</strong>
                            {step.dropoffs?.map(getOrderSpan)}
                        </div>)}
                        {((step.seconds!+step.duration!)<(nextStep?.seconds||0)) && (
                            <>
                                &nbsp;and {DurationType.secondsToHMS(nextStep!.seconds!-step.seconds!-step.duration!,60)} wait
                            </>
                        )}
                    </td>
                    <td>{DurationType.secondsToHMS(props.step.duration!,60)}</td>
                </tr>
            </tbody>
        </table>
    );
}

export interface VehicleManifestProps {
    style           : React.CSSProperties;
    vm              : ManifestType.VehicleManifest.Hydrated<OrderType.Order<string>>;
    vehicleSchedule : Record<string,[number,number,number][]>;
    driversById     : Record<string,UserType.User>;
}
const VehicleManifest : React.FC<VehicleManifestProps> = props => {
    const vm         = props.vm;
    return (
        <div className="col" style={props.style}>
            {vm.steps.map((step,sndx) => {
                return (
                    <RouteStep
                        key             = {sndx}
                        step            = {step}
                        nextStep        = {vm.steps[sndx+1]}
                        vehicleSchedule = {props.vehicleSchedule}
                        driversById     = {props.driversById}
                    />
                );
            })}
        </div>
    );
}

interface Props {
    type : ('fixedroute'|'ondemand');
}
interface State {
    vehicleManifests? : ManifestType.VehicleManifest.Hydrated<OrderType.Order<string>>[];
    vehiclesById?     : Record<number,VehicleType.Vehicle>;
    driversById?      : Record<number,UserType.User>;
    stepsDivStyle     : React.CSSProperties;
    manifestMoment    : dayjs.Dayjs;
}
export default class Manifests extends React.Component<Props,State> {

    private alert                  : Alert = new Alert();

    private getVehicleManifests( mmnt:dayjs.Dayjs ) : Promise<ManifestType.VehicleManifest.Hydrated<OrderType.Order<string>>[]|undefined> {
        return getApiPromise<ManifestType.VehicleManifest.Dehydrated<string>[]>(`/api/dispatcher/${this.props.type}/vehicleManifests`,"GET",undefined,{date:mmnt.format('YYYY-MM-DD')})
            .then( dehydratedVehicleManifests => {
                if( !dehydratedVehicleManifests || dehydratedVehicleManifests.err )
                    throw Error(`Cannot get vehicle manifests (${dehydratedVehicleManifests?.err||'unknown error'})`);
                return dehydratedVehicleManifests.map( vm => {
                    const usersById = rehash(vm.usersById,user=>UserType.hydrate(user));
                    return ManifestType.VehicleManifest.hydrate(
                        vm,
                        rehash(vm.ordersById,order=>OrderType.hydrate(order,usersById)),
                        vm.fixedRouteStopsById||{}
                    );
                }).sort((vm1,vm2)=>{
                    // VMs can be returned in any order, so let's sort VMs by vehicle_id
                    // to make this order consistent
                    return vm1.vehicle_id-vm2.vehicle_id;
                });
            })
            .catch( err => {
                this.alert.set(`Cannot init vehicle manifests (${err.message})`);
                return this.state.vehicleManifests;
            });
    }
    constructor( props:Props ) {
        super(props);
        this.state   = {
            stepsDivStyle    : {maxHeight:'750px',overflowY:'auto',scrollbarWidth:'none'},
            manifestMoment   : dayjs.tz(Date.now(),tectransit.agency.time_zone).startOf('day'),
        };
        Promise.all([
            this.getVehicleManifests(this.state.manifestMoment),
            getApiPromise<VehicleType.Vehicle[]>('/api/dispatcher/vehicles','GET')
                .then( vehicles => {
                    if( !vehicles || vehicles.err )
                        throw Error(`Cannot get vehicles (${vehicles?.err||'unknown error'})`);
                    return vehicles.reduce((acc,vehicle)=>({...acc,[vehicle._id]:vehicle}),{} as Record<number,VehicleType.Vehicle>);
                })
                .catch( err => {
                    this.alert.set(`Cannot init vehicles (${err.message})`);
                    return this.state.vehiclesById;
                }),
            getApiPromise<UserType.Dehydrated[]>('/api/dispatcher/users','GET',undefined, { inRoles : 'Driver' })
                .then( dehydrated => {
                    if( !dehydrated || dehydrated.err )
                        throw Error(`Cannot get drivers (${dehydrated?.err||'unknown error'})`);
                    return dehydrated.map(UserType.hydrate).reduce((acc,user) => {
                        acc[user._id] = user;
                        return acc;
                    },{} as Record<number,UserType.User>);
                })
                .catch( err => {
                    this.alert.set(`Cannot init drivers (${err.message})`);
                    return this.state.driversById;
                }),
        ]).then( ([vehicleManifests,vehiclesById,driversById])=>{
            this.setState({
                vehicleManifests,
                vehiclesById,
                driversById
            });
        })
    }
    render() {
        // @ts-expect-error
        window.toggleScrolls = ( onOff:boolean ) => {
            this.setState({
                stepsDivStyle : onOff ? {maxHeight:'600px',overflowY:'auto',scrollbarWidth:'none'} : {maxHeight:'none'}
            });
        }
        // TODO:
        // This is broken because we are using _current_ schedule to show manifests for a _past_.
        // I.e. somehow we need to save past driver schedules.
        const scheduleByVehicleId = AgencyType.rehashSchedule(
            tectransit.agency.schedule_by_driver_id,
            (driverId,vehicleId) => vehicleId,
            (driverId,vehicleId) => driverId
        );
        return MenuItem.withMenuItem(`Manifests for ${this.state.manifestMoment.format('YYYY-MM-DD')}`,( alert ) => {
            this.alert = alert;
            if( !this.state.vehicleManifests )
                return (<div style={{textAlign:'center'}}>Loading vehicle manifests...</div>);
            if( !this.state.vehiclesById )
                return (<div style={{textAlign:'center'}}>Loading vehicles...</div>);
            if( !this.state.driversById )
                return (<div style={{textAlign:'center'}}>Loading drivers...</div>);
            return (
                <div className="wrapper">
                    <VehicleManifestsType.VehicleManifests
                        vehicleManifests    = { this.state.vehicleManifests }
                        vehiclesById        = { this.state.vehiclesById }
                        driversById         = { this.state.driversById }
                        getStatus           = { ( ndx ) => {
                            const vm = this.state.vehicleManifests![ndx];
                            const problems = ManifestType.VehicleManifest.getProblems(vm);
                            if( problems.length<1 )
                                return (<span style={{color:'green'}}><strong>ok</strong></span>);
                            if( problems.length<2 )
                                return (<span style={{color:'red'}}><strong>{problems[0]}</strong></span>);
                            return (<span style={{color:'red'}}><strong>{problems.length} problems</strong></span>);
                        }}
                        getStops            = { ( ndx ) => {
                            const vm = this.state.vehicleManifests![ndx];
                            if( this.props.type==='fixedroute' ) {
                                return (
                                    <strong>{this.state.vehiclesById![vm.vehicle_id]?.fixed_route_id||'??'}</strong>
                                );
                            }
                            else {
                                const latlngs = (vm.steps||[]).filter(s=>!!s.latlng).map(s=>s.latlng!);
                                return (
                                    <a href={`https://www.google.com/maps/dir/${latlngs.map(ll=>encodeURI(LatLngType.toString(ll))).join('/')}`}>
                                        {latlngs.length}
                                    </a>
                                );
                            }
                        }}
                        getDistance         = { ( ndx ) => {
                            return DistanceType.fromMeters(this.state.vehicleManifests![ndx].distance).text;
                        }}
                        getSteps            = { ( ndx ) => {
                            const vm = this.state.vehicleManifests![ndx];
                            return <VehicleManifest
                                style           = {this.state.stepsDivStyle}
                                vm              = {vm}
                                vehicleSchedule = {scheduleByVehicleId[vm.vehicle_id]||{}}
                                driversById     = {this.state.driversById!}
                            />;
                        }}
                    />
                    <table style={{margin:'auto'}}>
                        <tbody>
                            <tr>
                                <td valign="top">
                                    <strong>See manifests for other days:</strong>
                                </td>
                                <td valign="top">
                                    <DatePicker
                                        selected            = {this.state.manifestMoment.toDate()}
                                        preventOpenOnFocus  = {true}
                                        onChange            = {(date:(Date|null)) => {
                                            if( !date )
                                                return;
                                            const manifestMoment = dayjs(date).tz(tectransit.agency.time_zone).startOf('day');
                                            this.getVehicleManifests(manifestMoment).then( vehicleManifests => {
                                                this.setState({
                                                    vehicleManifests,
                                                    manifestMoment
                                                });
                                            });
                                        }}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            );
        });
    }
}
